// D47 — Page /maintenance/{id} : détail intervention (5 sections)
import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { interventionsApi } from "@/lib/api";
import { useCan } from "@/hooks/useCan";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/toast";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Ban } from "lucide-react";
import { ht } from "@/lib/ht";
import { useT } from "@/lib/i18n";

export default function DetailInterventionPage() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const interventionId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
    const { toast } = useToast();
  const [showAnnuler, setShowAnnuler] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["intervention", interventionId],
    queryFn: () => interventionsApi.get(interventionId),
    enabled: !!interventionId,
  });

  const annulerMutation = useMutation({
    mutationFn: () => interventionsApi.annuler(interventionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["intervention", interventionId] });
      queryClient.invalidateQueries({ queryKey: ["interventions"] });
      toast({ title: ht("Intervention annulée"), variant: "success" });
      setShowAnnuler(false);
    },
    onError: (err) => {
      toast({
        title: ht("Erreur annulation"),
        description: (err as Error).message,
        variant: "error",
      });
      setShowAnnuler(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => interventionsApi.remove(interventionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["interventions"] });
      toast({ title: ht("Intervention supprimée"), variant: "success" });
      navigate("/maintenance");
    },
    onError: (err) => {
      toast({
        title: ht("Erreur suppression"),
        description: (err as Error).message,
        variant: "error",
      });
    },
  });

  if (isLoading) return <div className="p-8">{ht("Chargement...")}</div>;
  if (error) return <div className="p-8 text-red-600">{(error as Error).message}</div>;
  if (!data) return <div className="p-8">{ht("Intervention introuvable")}</div>;

  const canDelete = useCan("delete");

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/maintenance" className="text-sm text-muted-foreground hover:underline">
            ← Liste interventions
          </Link>
          <h1 className="font-mono text-2xl font-bold">{data.intervention_code}</h1>
        </div>
        <div className="flex gap-2">
          <Link to={`/maintenance/${interventionId}/edit`}>
            <Button variant="outline">{ht("Éditer")}</Button>
          </Link>
          {(data.statut === "planned" || data.statut === "in_progress") && (
            <Button
              variant="outline"
              onClick={() => setShowAnnuler(true)}
              disabled={annulerMutation.isPending}
            >
              <Ban className="mr-1 h-4 w-4" />{ht("Annuler")}
            </Button>
          )}
          {canDelete && (
            <Button
              variant="destructive"
              onClick={() => setShowDelete(true)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Suppression..." : "Supprimer"}
            </Button>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={showAnnuler}
        title={ht("Annuler cette intervention ?")}
        description={`L'intervention ${data.intervention_code} passera en statut 'cancelled'. Les indicateurs de l'engin ne seront pas modifiés.`}
        confirmLabel={ht("Annuler l'intervention")}
        variant="destructive"
        loading={annulerMutation.isPending}
        onConfirm={() => annulerMutation.mutate()}
        onCancel={() => setShowAnnuler(false)}
      />

      <ConfirmDialog
        open={showDelete}
        title={ht("Supprimer définitivement cette intervention ?")}
        description={`Cette action supprimera ${data.intervention_code} et toutes ses données associées. Irréversible.`}
        confirmLabel={ht("Supprimer")}
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => setShowDelete(false)}
      />

      {/* Section 1 : Informations */}
      <Card>
        <CardHeader>
          <CardTitle>Informations</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-3 text-sm">
          <Field label={ht("Type")} value={data.type} />
          <Field label={ht("Statut")} value={data.statut} />
          <Field
            label={ht("Date début")}
            value={new Date(data.date_debut).toLocaleString("fr-FR")}
          />
          <Field
            label={ht("Date fin")}
            value={new Date(data.date_fin).toLocaleString("fr-FR")}
          />
          <Field label={ht("Intervenant")} value={`#${data.intervenant_id}`} />
          <Field label={ht("Superviseur")} value={`#${data.superviseur_id}`} />
          <Field label={ht("Engin")} value={`#${data.engin_id}`} />
          <Field
            label={ht("DA créée ?")}
            value={data.demande_creaset ? t("common.yes") : t("common.no")}
          />
          <div className="col-span-2">
            <Field label={ht("Résumé")} value={data.resume ?? "-"} />
          </div>
        </CardContent>
      </Card>

      {/* Section 2 : Indicateurs */}
      <Card>
        <CardHeader>
          <CardTitle>Indicateurs ({data.indicators.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {data.indicators.length === 0 ? (
            <p className="text-muted-foreground text-sm">{ht("Aucun indicateur")}</p>
          ) : (
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                  <th className="px-3 py-2">{ht("Indicateur")}</th>
                  <th className="px-3 py-2">{ht("Avant")}</th>
                  <th className="px-3 py-2">{ht("Après")}</th>
                  <th className="px-3 py-2">Δ</th>
                </tr>
              </thead>
              <tbody>
                {data.indicators.map((i) => (
                  <tr key={i.id} className="border-b">
                    <td className="px-3 py-2 font-mono">{i.indicator_key}</td>
                    <td className="px-3 py-2">{i.value_before ?? "-"}</td>
                    <td className="px-3 py-2">{i.value_after}</td>
                    <td className="px-3 py-2 font-semibold">{i.delta.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </CardContent>
      </Card>

      {/* Section 3 : Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Actions ({data.actions.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {data.actions.length === 0 ? (
            <p className="text-muted-foreground text-sm">{ht("Aucune action")}</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {data.actions.map((a) => (
                <li key={a.id} className="border-b pb-1">
                  <span className="font-semibold">{a.action}</span>
                  {a.description && (
                    <span className="text-muted-foreground"> — {a.description}</span>
                  )}
                  {a.duree_min && (
                    <span className="ml-2 rounded bg-muted px-2 py-0.5 text-xs">
                      {a.duree_min} min
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Section 4 : Articles utilisés (placeholder) */}
      <Card>
        <CardHeader>
          <CardTitle>{ht("Articles utilisés")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Section 4 — implémentation Sprint 3 (Module Stocks D88+)
          </p>
        </CardContent>
      </Card>

      {/* Section 5 : DA liée */}
      <Card>
        <CardHeader>
          <CardTitle>{ht("Demande d'achat liée")}</CardTitle>
        </CardHeader>
        <CardContent>
          {data.demande_creaset ? (
            <p className="text-sm text-amber-700">
              ⚠️ Cette intervention a déclenché la création d'une demande d'achat.
              Voir détail dans le module Demandes d'achat (Sprint 3).
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">{ht("Aucune demande d'achat liée.")}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}